import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { authApiClient } from "@/lib/auth";
import { queryClient } from "@/lib/query-client";
import { useCompanyStore } from "@/store";
import { Button, Card, CardContent } from "@/components/ui";

interface AcceptInviteSearch {
  invite_id?: string;
}

export const Route = createFileRoute("/accept-invite")({
  validateSearch: (search: Record<string, unknown>): AcceptInviteSearch => ({
    invite_id: typeof search.invite_id === "string" ? search.invite_id : undefined,
  }),
  beforeLoad: ({ context, location }) => {
    if (!context.auth.isAuthenticated) {
      // `location.href` — never `pathname + search`, since `search` is
      // TanStack's parsed OBJECT and concatenating it yields
      // "[object Object]", dropping the invite id the whole visit is about.
      throw redirect({
        to: "/login",
        search: { redirect: location.href },
      });
    }
  },
  component: AcceptInvitePage,
});

function AcceptInvitePage() {
  const { t } = useTranslation("company");
  const navigate = useNavigate();
  const { invite_id: inviteId } = Route.useSearch();
  const email = useCompanyStore((s) => s.user?.email ?? "");

  // Isolation carve-out: company-member-invite accept is a MAIN-spec
  // personal-context op (no org scope, server-side email match — the
  // org/tester invite recipe). It belongs to the legal authApiClient
  // personal-context surface documented in lib/auth.tsx; everything else
  // rides /api/v1/company.
  const acceptMutation = useMutation({
    mutationFn: async () => {
      if (!inviteId) throw new Error("missing invite id");
      const { error } = await authApiClient.POST("/company-member-invites/{inviteId}/accept", {
        params: { path: { inviteId } },
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["company", "me"] });
      void navigate({ to: "/company/dispatch" });
    },
  });

  const errorKey =
    acceptMutation.isError &&
    (acceptMutation.error as { code?: string }).code === "company_member_invite_email_mismatch"
      ? "acceptInvite.emailMismatch"
      : "acceptInvite.invalid";

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface p-4">
      <Card>
        <CardContent>
          <div className="max-w-md space-y-4 p-6 text-center">
            <h1 className="text-2xl font-bold text-on-surface">{t("acceptInvite.title")}</h1>
            <p className="text-sm text-muted">{t("acceptInvite.body", { email })}</p>
            {acceptMutation.isError && <p className="text-sm text-danger">{t(errorKey)}</p>}
            {acceptMutation.isSuccess ? (
              <p className="text-sm text-on-surface">{t("acceptInvite.accepted")}</p>
            ) : (
              <Button
                onClick={() => acceptMutation.mutate()}
                disabled={!inviteId || acceptMutation.isPending}
              >
                {t("acceptInvite.accept")}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
